import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const DemoSection = () => {
  const formRef = useRef();
  const [formState, setFormState] = useState({
    name: '',
    company: '',
    email: '',
    role: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [id]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formState.name.trim()) newErrors.name = 'Name is required';
    if (!formState.company.trim()) newErrors.company = 'Company is required';
    
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formState.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formState.role || formState.role === '') {
      newErrors.role = 'Please select your role';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Replace these with your actual EmailJS service ID, template ID, and public key
      // You'll need to sign up at https://www.emailjs.com/ and create a template
      const serviceId = 'service_panx9eh';
      const templateId = 'template_223263r';
      const publicKey = 'qXoBa1vlEQeSEUeIM';
      
      await emailjs.sendForm(
        serviceId,
        templateId,
        formRef.current,
        publicKey
      );
      
      // Reset form on success
      setFormState({
        name: '',
        company: '',
        email: '',
        role: '',
        message: ''
      });
      
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! Your demo request has been received. We\'ll contact you shortly.'
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({
        type: 'error',
        message: 'There was an error submitting your request. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="request-demo" className="py-20 bg-gradient-to-br from-purple-700 to-purple-900 text-white relative overflow-hidden">
      {/* Animated background shapes */}
      <motion.div 
        className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full opacity-20"
        animate={{ 
          x: [0, 30, 0],
          y: [0, -30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 15,
          ease: "easeInOut"
        }}
      ></motion.div>
      
      <motion.div 
        className="absolute bottom-0 left-10 w-64 h-64 bg-indigo-500 rounded-full opacity-20"
        animate={{ 
          x: [0, -20, 0],
          y: [0, 20, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{ 
          repeat: Infinity,
          duration: 12,
          ease: "easeInOut"
        }}
      ></motion.div>
      
      <div className="container mx-auto relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-4xl font-bold mb-6"
          >
            Ready to Transform Your Data Quality?
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-xl text-purple-100 mb-10"
          >
            See how Qupid can help your team build trust in your data with comprehensive
            observability, monitoring, and quality assurance.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-xl p-8 shadow-xl"
          >
            <h3 className="text-purple-800 text-2xl font-bold mb-4">Request a Personalized Demo</h3>
            <p className="text-gray-600 mb-6">
              Our team will show you how Qupid works with your specific data sources and use cases.
            </p>
            
            {submitStatus?.type === 'success' ? (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md mb-4">
                <p>{submitStatus.message}</p>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 text-left mb-1">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className={`w-full px-4 py-2 rounded-md border text-gray-800 ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1 text-left">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 text-left mb-1">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formState.company}
                      onChange={handleChange}
                      placeholder="Your company name"
                      className={`w-full px-4 py-2 rounded-md border text-gray-800 ${errors.company ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    />
                    {errors.company && <p className="text-red-500 text-xs mt-1 text-left">{errors.company}</p>}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 text-left mb-1">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className={`w-full px-4 py-2 rounded-md border text-gray-800 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1 text-left">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 text-left mb-1">Role</label>
                    <select
                      id="role"
                      name="role"
                      value={formState.role}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 rounded-md border text-gray-800 ${errors.role ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    >
                      <option value="" disabled>Select your role</option>
                      <option value="data_engineer">Data Engineer</option>
                      <option value="data_scientist">Data Scientist</option>
                      <option value="data_analyst">Data Analyst</option>
                      <option value="engineering_manager">Engineering Manager</option>
                      <option value="executive">Executive</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.role && <p className="text-red-500 text-xs mt-1 text-left">{errors.role}</p>}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 text-left mb-1">What are your main data quality challenges?</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Tell us about your current data quality needs and challenges..."
                    className={`w-full px-4 py-2 rounded-md border text-gray-800 ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  ></textarea>
                  {errors.message && <p className="text-red-500 text-xs mt-1 text-left">{errors.message}</p>}
                </div>
                
                {submitStatus?.type === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
                    <p>{submitStatus.message}</p>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-md transition-colors duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : 'Schedule Your Demo'}
                </button>
              </form>
            )}
            
            <p className="text-sm text-gray-500 mt-4">
              By submitting this form, you agree to our <a href="/privacy" className="underline">Privacy Policy</a>.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection; 